import { useState, type ReactNode } from "react";
import { cn } from "../lib/utils";

export interface TreeNode {
  id: string;
  label: ReactNode;
  icon?: ReactNode;
  badge?: ReactNode;
  children?: TreeNode[];
  disabled?: boolean;
}

interface TreeViewProps {
  nodes: TreeNode[];
  selected?: string;
  onSelect?: (id: string) => void;
  defaultExpanded?: string[];
  className?: string;
}

function TreeNodeItem({
  node,
  depth,
  selected,
  onSelect,
  expanded,
  onToggle,
}: {
  node: TreeNode;
  depth: number;
  selected?: string;
  onSelect?: (id: string) => void;
  expanded: Set<string>;
  onToggle: (id: string) => void;
}) {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expanded.has(node.id);
  const isSelected = selected === node.id;

  return (
    <div className="tree-node">
      <button
        className={cn("tree-item", isSelected && "tree-item-selected", node.disabled && "tree-item-disabled")}
        style={{ paddingLeft: `${0.5 + depth * 1.25}rem` }}
        onClick={() => {
          if (!node.disabled) {
            if (hasChildren) onToggle(node.id);
            onSelect?.(node.id);
          }
        }}
        disabled={node.disabled}
        aria-expanded={hasChildren ? isExpanded : undefined}
        type="button"
      >
        {hasChildren ? (
          <span className={cn("tree-chevron", isExpanded && "tree-chevron-open")}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6" /></svg>
          </span>
        ) : (
          <span className="tree-chevron-spacer" />
        )}
        {node.icon && <span className="tree-icon">{node.icon}</span>}
        <span className="tree-label">{node.label}</span>
        {node.badge && <span className="tree-badge">{node.badge}</span>}
      </button>

      {hasChildren && isExpanded && (
        <div className="tree-children">
          {node.children!.map(child => (
            <TreeNodeItem
              key={child.id}
              node={child}
              depth={depth + 1}
              selected={selected}
              onSelect={onSelect}
              expanded={expanded}
              onToggle={onToggle}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function TreeView({
  nodes,
  selected,
  onSelect,
  defaultExpanded = [],
  className,
}: TreeViewProps) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set(defaultExpanded));

  const toggle = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div className={cn("tree-root", className)} role="tree">
      {nodes.map(node => (
        <TreeNodeItem
          key={node.id}
          node={node}
          depth={0}
          selected={selected}
          onSelect={onSelect}
          expanded={expanded}
          onToggle={toggle}
        />
      ))}
    </div>
  );
}
